import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './HarborAccents.css';
import './HarborNav.css';
import './HarborWood.css';
import './HarborAurora.css';
import { MenuPanel, useMenuPanel } from './MenuPanel';
import { spring, paint, kick, rootPx } from './springs';
import { LIPS, useAccent, styleOf } from './HarborAccents';
import { bakeAurora } from './HarborAurora';
import { ThemeModeContext } from '../../App';

// The Harbor redesign nav. It draws the same navItems, selectedKeys and
// navigate() that Layout hands the classic Menu, so every permission rule,
// route and highlight stays exactly as Layout defines it -- this file only
// changes how the bar looks and moves. Ported from design/nav-prototype.html.

// ---------- which look: classic (default) or the redesign, remembered per browser ----------
const STYLE_KEY = 'harbor-nav-style';
export function useNavStyle() {
  const [style, setStyleState] = useState(() => {
    try { return localStorage.getItem(STYLE_KEY) === 'harbor' ? 'harbor' : 'classic'; } catch { return 'classic'; }
  });
  const setStyle = useCallback((v) => {
    setStyleState(v);
    try { localStorage.setItem(STYLE_KEY, v); } catch { /* private window */ }
  }, []);
  return [style, setStyle];
}

const HUB_KEY = 'agent-summary-group';

// What the hologram says for each section. Pages show "Section › Page".
const SECTION_DESC = {
  'agent-summary-group': 'Your space: scores, coaching, absences and team recognition.',
  'interactions-group': 'Search calls and track campaign performance.',
  'evaluations-group': 'Evaluations, coaching, errors and compliance work.',
  'oversight-group': 'Team-wide monitoring: adherence, staffing, absences, incidents, projects.',
  'recruit-group': 'Hiring, headcount, attrition and staffing forecasts.',
  'ai-group': "Chat with Harbor's AI assistant.",
  '/leaderboard': 'See who is leading the team rankings.',
  'admin-group': 'Configure users, teams, campaigns and data uploads.',
};
const BELL_DESC = 'Alerts and notifications for you.';
const RECENT_DESC = 'Pages you visited recently.';
const PROFILE_DESC = 'Your profile and settings, and log out.';

// On first draw the three lit edges start dark and fill left to right, each at its own pace.
const REVEAL_MS = [2300, 3170, 3530];
// Edge opacity at the far left, just before and after My Hub, and at the far right (Gold's;
// the LED accents set their own in LIPS so the strip stays lit to the ends).
const EDGE_STOPS = [.12, .55, .5, .08];

// Northern Lights' pine: drawn once, reused at every size (layoutPines).
const PINE = 'M20 0C21 8 24 15 27 21L23 21C26 29 30 37 33 44L27 44C30 53 34 61 37 68L30 68C33 77 36 85 40 92'
  + 'L22 92L22 100L18 100L18 92L0 92C4 85 7 77 10 68L3 68C6 61 10 53 13 44L7 44C10 37 14 29 17 21L13 21C16 15 19 8 20 0Z';

// What pins under the bar while a long table scrolls (HarborColors.css): Ant Design's sticky
// table headers, and sticky card titles and table labels.
const PINNED = '.ant-table-sticky-holder, .sticky-table-card > .ant-card-head, .sticky-table-label';

const CHEV_L = <svg viewBox="0 0 12 12"><path d="M7.5 2.5 4 6l3.5 3.5" /></svg>;
const CHEV_R = <svg viewBox="0 0 12 12"><path d="M4.5 2.5 8 6l-3.5 3.5" /></svg>;

// ---------- trough glide: a row wider than its trough follows the mouse ----------
const glide = (t) => t._hbG || (t._hbG = { cur: 0, tgt: 0, raf: 0 });
const trackOf = (t) => t.querySelector('.hb-track');
const glideMax = (t) => Math.max(0, trackOf(t).offsetWidth - t.clientWidth);
function applyGlide(t) {
  const st = glide(t);
  trackOf(t).style.transform = st.cur ? `translateX(${(-st.cur).toFixed(1)}px)` : '';
  t.classList.toggle('hb-more-l', st.cur > 1);
  t.classList.toggle('hb-more-r', st.cur < glideMax(t) - 1);
}
function glideTo(t, v, instant, reduced) {
  const st = glide(t);
  st.tgt = Math.max(0, Math.min(glideMax(t), v));
  if (instant || reduced) { cancelAnimationFrame(st.raf); st.raf = 0; st.cur = st.tgt; applyGlide(t); return; }
  const step = () => {
    st.cur += (st.tgt - st.cur) * .16;
    if (Math.abs(st.tgt - st.cur) < .5) st.cur = st.tgt;
    applyGlide(t);
    st.raf = st.cur !== st.tgt ? requestAnimationFrame(step) : 0;
  };
  if (!st.raf) st.raf = requestAnimationFrame(step);
}

export default function HarborNav({ items, selectedKeys, onNavigate, user, dark, onLogout, onProfile, bell, recent, extras, create }) {
  const reduced = useMemo(() => !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const navRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const hubRef = useRef(null);
  const homeRef = useRef(null);
  const homeBodyRef = useRef(null);
  const holoRef = useRef(null);
  const tipRef = useRef(null);
  const terrRef = useRef(null);
  const pinesRef = useRef(null);
  const lightRef = useRef(null);
  const timers = useRef({});
  const accent = useAccent();
  const { setDark } = useContext(ThemeModeContext);
  // Pointer work runs at most once per frame (mice can report up to 1000 moves a second).
  const pointer = useRef({ x: 0, trail: null, raf: 0, at: 0 });
  const onPointerFrame = () => {
    const pt = pointer.current;
    pt.raf = 0;
    if (lightRef.current) lightRef.current.style.transform = `translate3d(${pt.x}px,0,0)`;
    const t = pt.trail;
    if (!t) return;
    const m = glideMax(t);
    if (!m) return;
    const r = t.getBoundingClientRect(), pad = Math.min(r.width * .2, 2.5 * rootPx());
    const f = Math.max(0, Math.min(1, (pt.x - r.left - pad) / (r.width - 2 * pad)));
    glideTo(t, f * m, false, reduced);
  };
  const queuePointer = (x, trail) => {
    const pt = pointer.current;
    pt.x = x; pt.trail = trail;
    const now = performance.now();
    if (pt.raf && now - pt.at < 100) return;
    cancelAnimationFrame(pt.raf); // a frame that never arrived must not block the next one
    pt.at = now;
    pt.raf = requestAnimationFrame(onPointerFrame);
  };
  const revealStart = useRef(0);

  const hubItem = items.find((i) => i.key === HUB_KEY);
  const others = items.filter((i) => i.key !== HUB_KEY);
  const nLeft = Math.floor(others.length / 2);
  const leftItems = others.slice(0, nLeft);
  const rightItems = others.slice(nLeft);
  const initials = (user?.DisplayName || '').split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '·';

  // While the redesign is on, <html> carries .hb-on so the Harbor colours also reach
  // pop-ups (modals, dropdowns) that render outside the layout.
  useEffect(() => {
    document.documentElement.classList.add('hb-on');
    return () => document.documentElement.classList.remove('hb-on');
  }, []);
  // ...and data-hb-accent, which picks the colour profile in HarborAccents.css, and
  // data-hb-style (yacht, wood or aurora), which picks the bar's shape (HarborWood.css,
  // HarborAurora.css).
  useEffect(() => {
    const el = document.documentElement;
    el.dataset.hbAccent = accent;
    el.dataset.hbStyle = styleOf(accent);
    return () => { delete el.dataset.hbAccent; delete el.dataset.hbStyle; };
  }, [accent]);
  // Northern Lights is night only, and bakes its aurora the first time it shows.
  useEffect(() => {
    if (styleOf(accent) !== 'aurora') return;
    bakeAurora();
    if (!dark) setDark(true);
  }, [accent, dark, setDark]);

  // Rajdhani, only when the redesign is in use.
  useEffect(() => {
    if (document.getElementById('hb-font')) return;
    const link = document.createElement('link');
    link.id = 'hb-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  // ---------- hover lift ----------
  const lift = (el, up) => { const s = spring(el, .16, .74); s.ty = up ? -.1875 * rootPx() : 0; kick(s, reduced); };

  // ---------- My Hub as tooltip screen ----------
  const glitch = () => {
    const h = homeRef.current;
    if (reduced || !h) return;
    h.classList.remove('hb-glitch'); void h.offsetWidth; h.classList.add('hb-glitch');
    setTimeout(() => h.classList.remove('hb-glitch'), 300);
  };
  const tell = (text) => {
    const h = homeRef.current, tip = tipRef.current, t = timers.current;
    if (!h || !tip) return;
    clearTimeout(t.back); clearInterval(t.type);
    if (!h.classList.contains('hb-telling')) glitch();
    h.classList.add('hb-telling');
    if (reduced) { tip.textContent = text; return; }
    const step = Math.max(1, Math.ceil(text.length / 22));
    let n = 0;
    tip.textContent = '';
    t.type = setInterval(() => { n += step; tip.textContent = text.slice(0, n); if (n >= text.length) clearInterval(t.type); }, 14);
  };
  const idleSoon = () => {
    const t = timers.current;
    clearTimeout(t.back);
    t.back = setTimeout(() => {
      clearInterval(t.type);
      const h = homeRef.current;
      if (h && h.classList.contains('hb-telling')) { glitch(); h.classList.remove('hb-telling'); }
    }, 450);
  };

  // ---------- drop-down panel ----------
  const panel = useMenuPanel({ barRef: navRef, reduced });
  const { openMenu, scheduleClose, hoverOpen, clickToggle } = panel;

  // ---------- terraced lips under the bar, with the lit edge reveal ----------
  const layoutTerraces = useCallback(() => {
    const nav = navRef.current, hub = hubRef.current, terr = terrRef.current;
    if (!nav || !hub || !terr) return;
    // Wood's plank has no lips
    if (!LIPS[accent]) { terr.innerHTML = ''; terr.style.height = '0px'; return; }
    const L = LIPS[accent][dark ? 'dark' : 'light'], st = L.stops || EDGE_STOPS;
    const W = nav.clientWidth, H = nav.offsetHeight, sc = H / 64;
    const hx = hub.offsetLeft + hub.offsetWidth / 2, halfW = hub.offsetWidth / 2;
    const f = Math.max(.05, Math.min(.95, hx / W));
    let maxY = 0;
    const edge = (i) => {
      const yL = H + (2 + i * 5) * sc, yR = H + i * 4 * sc, yD = H + (16 + i * 9) * sc;
      const aL = halfW + (35 + i * 45) * sc, aR = halfW + (75 + i * 60) * sc;
      const c1 = Math.min(hx * .45, hx - aL - 10), c4 = Math.max(hx + (W - hx) * .55, hx + aR + 10);
      maxY = Math.max(maxY, yD);
      return { yL, d: `C${c1.toFixed(1)},${yL.toFixed(1)} ${(hx - aL).toFixed(1)},${yD.toFixed(1)} ${hx.toFixed(1)},${yD.toFixed(1)} C${(hx + aR).toFixed(1)},${yD.toFixed(1)} ${c4.toFixed(1)},${yR.toFixed(1)} ${W},${yR.toFixed(1)}` };
    };
    let defs = `<defs><linearGradient id="hb-edgeG" x1="0" x2="1">
      <stop offset="0" stop-color="${L.edge[0]}" stop-opacity="${st[0]}"/>
      <stop offset="${Math.max(0, f - .14)}" stop-color="${L.edge[1]}" stop-opacity="${st[1]}"/>
      <stop offset="${f}" stop-color="${L.edge[2]}" stop-opacity="1"/>
      <stop offset="${Math.min(1, f + .22)}" stop-color="${L.edge[1]}" stop-opacity="${st[2]}"/>
      <stop offset="1" stop-color="${L.edge[0]}" stop-opacity="${st[3]}"/></linearGradient>
      <filter id="hb-blur" x="-5%" y="-200%" width="110%" height="500%"><feGaussianBlur stdDeviation="${(2.4 * Math.sqrt(sc)).toFixed(2)}"/></filter>
      <filter id="hb-halo" x="-5%" y="-400%" width="110%" height="900%"><feGaussianBlur stdDeviation="${(6 * Math.sqrt(sc)).toFixed(2)}"/></filter>`;
    L.fills.forEach((c, i) => {
      defs += `<linearGradient id="hb-lip${i}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${L.top}"/><stop offset=".75" stop-color="${c}"/><stop offset="1" stop-color="${L.bottom}"/></linearGradient>`;
    });
    defs += '</defs>';
    let body = '';
    const sw = Math.sqrt(sc);
    for (let i = 2; i >= 0; i--) {
      const e = edge(i);
      body += `<path d="M0,${H - 2} L0,${e.yL.toFixed(1)} ${e.d} L${W},${H - 2} Z" fill="url(#hb-lip${i})"/>`;
      body += `<path d="M0,${e.yL.toFixed(1)} ${e.d}" fill="none" stroke="${L.unlit}" stroke-width="${(1.3 * sw).toFixed(2)}"/>`;
      // LED accents: a wide, soft halo around the strip
      if (L.halo) body += `<path class="hb-edge" data-i="${i}" pathLength="1000" d="M0,${e.yL.toFixed(1)} ${e.d}" fill="none" stroke="${L.halo}" stroke-width="${(10 * sw).toFixed(2)}" stroke-linecap="round" filter="url(#hb-halo)" opacity="${.9 - i * .2}"/>`;
      body += `<path class="hb-edge" data-i="${i}" pathLength="1000" d="M0,${e.yL.toFixed(1)} ${e.d}" fill="none" stroke="${L.glow}" stroke-width="${(4 * sw).toFixed(2)}" stroke-linecap="round" filter="url(#hb-blur)" opacity="${.9 - i * .25}"/>`;
      body += `<path class="hb-edge" data-i="${i}" pathLength="1000" d="M0,${e.yL.toFixed(1)} ${e.d}" fill="none" stroke="url(#hb-edgeG)" stroke-width="${(1.3 * sw).toFixed(2)}" stroke-linecap="round" opacity="${1 - i * .22}"/>`;
    }
    const h = Math.ceil(maxY + (L.halo ? 30 : 24));
    terr.style.height = h + 'px';
    terr.setAttribute('viewBox', `0 0 ${W} ${h}`);
    terr.innerHTML = defs + body;
    // the reveal continues from where it was if the bar is redrawn
    if (!revealStart.current) revealStart.current = performance.now();
    const elapsed = performance.now() - revealStart.current;
    if (!reduced) {
      terr.querySelectorAll('.hb-edge').forEach((path) => {
        path.setAttribute('stroke-dasharray', '1000 1000');
        path.animate([{ strokeDashoffset: 1000 }, { strokeDashoffset: 0 }],
          { duration: REVEAL_MS[path.dataset.i], delay: -elapsed, fill: 'forwards' });
      });
    }
  }, [dark, accent, reduced]);

  // ---------- Northern Lights: the pine skyline under the bar ----------
  // The bar ends in a curve, lowest at the sides and rising toward My Hub. Pines of mixed
  // sizes (never two of nearly the same height side by side) stand on it whole; undergrowth
  // between them keeps the lower forest solid, and a line of aurora light runs along the
  // treetops. Drawn to the bar's width so nothing stretches; redrawn only when it changes size.
  const layoutPines = useCallback(() => {
    const nav = navRef.current, hub = hubRef.current, svg = pinesRef.current;
    if (!nav || !hub || !svg) return;
    if (styleOf(accent) !== 'aurora') { svg.innerHTML = ''; return; }
    const W = nav.clientWidth, top = 14, mid = 46, edge = 86, H = top + edge + 4;
    const hx = hub.offsetLeft + hub.offsetWidth / 2;
    const curve = (x) => {
      const d = Math.min(1, Math.max(0, x < hx ? (hx - x) / hx : (x - hx) / (W - hx)));
      return top + mid + (edge - mid) * Math.pow(d, 1.6);
    };
    const f = (n) => n.toFixed(1);
    let line = '';
    for (let x = 0; x <= W; x += 6) line += `${x ? 'L' : 'M'}${x},${f(curve(x))}`;
    line += `L${W},${f(curve(W))}`;
    const back = line.replace(/^M/, 'L').split('L').filter(Boolean).reverse();
    const sky = `M0,0H${W}V${f(curve(W))}${back.map((pt) => 'L' + pt).join('')}Z`;
    let seed = 917, prev = 0, trees = '';
    const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    const pick = () => {
      let h;
      do {
        const r = rnd();
        h = r < .22 ? 18 + rnd() * 10 : r < .74 ? 29 + rnd() * 15 : 45 + rnd() * 17;
      } while (Math.abs(h - prev) < 7);
      prev = h;
      return h;
    };
    const stand = [];
    for (let x = -14; x < W + 14;) {
      const cap = curve(x) + 3.5 - top - 6;   // the middle ones stay clear of My Hub
      let h = pick();
      if (h > cap) h = cap * (.6 + rnd() * .4);
      const w = h * (.36 + rnd() * .1);
      const base = curve(x) + h * .08;        // the edge meets the lowest branches; the trunk tucks below
      stand.push({ cx: x, h, w });
      trees += `<use href="#hb-nl-pine" x="${f(x - w / 2)}" y="${f(base - h)}" width="${f(w)}" height="${f(h)}"/>`;
      x += w * (.45 + rnd() * .4);
    }
    const under = (x) => {
      let g = 4;
      for (const t of stand) {
        const d = Math.abs(x - t.cx) / (t.w * .75);
        if (d < 1) g = Math.max(g, t.h * .4 * (1 - d * d * .45));
      }
      return g;
    };
    let ground = '<path d="';
    for (let x = 0; x <= W; x += 4) ground += `${x ? 'L' : 'M'}${x},${f(curve(x))}`;
    for (let x = W; x >= 0; x -= 4) ground += `L${x},${f(curve(x) - under(x))}`;
    ground += 'Z"/>';
    svg.setAttribute('width', W); svg.setAttribute('height', H); svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    // the sky overlaps the bar by 2px so no hairline of page shows at the seam
    svg.innerHTML = `<defs><symbol id="hb-nl-pine" viewBox="0 0 40 100" preserveAspectRatio="none"><path d="${PINE}"/></symbol>`
      + `<clipPath id="hb-nl-sky"><path d="${sky}"/></clipPath></defs>`
      + `<rect x="0" y="${top - 2}" width="${W}" height="${H - top + 2}" style="fill:var(--hb-sky-end)" clip-path="url(#hb-nl-sky)"/>`
      + `<g clip-path="url(#hb-nl-sky)"><g style="fill:var(--hb-pine-rim)" transform="translate(0 -1.5)">${trees}</g>`
      + `<g style="fill:var(--hb-pine)">${trees}${ground}</g></g>`;
  }, [accent]);

  // Pinned table headers stop just under the bar, where the pines hang. As one scrolls up to
  // the bar, the pines slide up under the bar ahead of it, as if it pushed them; they come back
  // when it moves on. A transform only, worked out at most once a frame while scrolling.
  useEffect(() => {
    const nav = navRef.current, svg = pinesRef.current;
    if (styleOf(accent) !== 'aurora' || !nav || !svg) return undefined;
    const DEPTH = 86; // how far the pines reach below the bar, at the sides
    let raf = 0, last = 0;
    const update = () => {
      raf = 0;
      const barH = nav.offsetHeight;
      let push = 0;
      document.querySelectorAll(PINNED).forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height && r.bottom > barH) push = Math.max(push, Math.min(DEPTH, barH + DEPTH - r.top));
      });
      if (push !== last) {
        last = push;
        svg.style.transform = push > 0 ? `translate3d(0,${-push.toFixed(1)}px,0)` : '';
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    // capture: pages that scroll inside a box count too
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
      svg.style.transform = '';
    };
  }, [accent]);

  // ---------- fit: troughs glide instead of collapsing; only huge text in a narrow window goes "mini" ----------
  const fit = useCallback(() => {
    const nav = navRef.current, L = leftRef.current, R = rightRef.current;
    if (!nav || !L || !R) return;
    nav.classList.remove('hb-mini');
    const rp = rootPx();
    const tooNarrow = (t) => [...t.querySelectorAll('.hb-key')].some((k) => k.offsetWidth + 2.5 * rp > t.clientWidth);
    if (tooNarrow(L) || tooNarrow(R)) nav.classList.add('hb-mini');
    glideTo(L, glideMax(L), true, reduced); // resting: the keys nearest My Hub show
    glideTo(R, 0, true, reduced);
  }, [reduced]);

  const itemKeys = items.map((i) => i.key).join('|');
  useLayoutEffect(() => { fit(); layoutTerraces(); layoutPines(); }, [itemKeys, fit, layoutTerraces, layoutPines]);
  useEffect(() => {
    const nav = navRef.current;
    let rz = 0, late = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rz); clearTimeout(late);
      rz = requestAnimationFrame(() => { fit(); layoutTerraces(); layoutPines(); late = setTimeout(() => { layoutTerraces(); layoutPines(); }, 340); });
    });
    ro.observe(nav);
    if (document.fonts) document.fonts.ready.then(() => { fit(); layoutTerraces(); layoutPines(); });
    return () => { ro.disconnect(); cancelAnimationFrame(rz); clearTimeout(late); };
  }, [fit, layoutTerraces, layoutPines]);

  // Keys drop into their troughs when the bar first appears or the set of sections changes.
  useEffect(() => {
    if (reduced) return;
    [...navRef.current.querySelectorAll('.hb-track .hb-key')].forEach((b, i) => {
      const s = spring(b, .16, .74);
      s.y = -22; paint(s);
      setTimeout(() => kick(s, reduced), 120 + i * 55);
    });
  }, [itemKeys, reduced]);

  useEffect(() => () => {
    Object.values(timers.current).forEach((id) => { clearTimeout(id); clearInterval(id); });
    cancelAnimationFrame(pointer.current.raf);
  }, []);

  const onTrailMove = (e) => { e.stopPropagation(); queuePointer(e.clientX, e.currentTarget); };
  const onTrackFocus = (e) => {
    const k = e.target.closest('.hb-key'), t = e.currentTarget.parentElement;
    if (k) glideTo(t, k.offsetLeft - (t.clientWidth - k.offsetWidth) / 2, false, reduced);
  };

  const renderKey = (item) => {
    const hasMenu = !!(item.children && item.children.length);
    const open = panel.isOpen(item.key);
    return (
      <button
        key={item.key}
        type="button"
        className={`hb-key${selected.has(item.key) ? ' hb-active' : ''}`}
        aria-haspopup={hasMenu || undefined}
        aria-expanded={hasMenu ? !!open : undefined}
        onPointerEnter={(e) => { lift(e.currentTarget, true); tell(SECTION_DESC[item.key] || item.label); if (hasMenu) hoverOpen(e.currentTarget, item); }}
        onPointerLeave={(e) => { lift(e.currentTarget, false); idleSoon(); if (hasMenu) scheduleClose(); }}
        onFocus={(e) => { if (hasMenu && e.currentTarget.matches(':focus-visible')) openMenu(e.currentTarget, item, 'click'); }}
        onClick={(e) => (hasMenu ? clickToggle(e.currentTarget, item) : onNavigate(item.key))}
      >
        {item.icon}
        <span className="hb-lbl">{item.label}</span>
      </button>
    );
  };

  const trail = (side, ref, list) => (
    <nav className={`hb-trail hb-trail-${side}`} ref={ref} onPointerMove={onTrailMove} aria-label={side === 'left' ? 'Sections' : 'More sections'}>
      <div className="hb-pulse"><div className="hb-pulse-run" /></div>
      <div className="hb-track" onFocus={onTrackFocus}>{list.map(renderKey)}</div>
      <div className="hb-fade hb-fade-l">{CHEV_L}</div>
      <div className="hb-fade hb-fade-r">{CHEV_R}</div>
    </nav>
  );

  // Profile menu: same panel, with actions instead of routes.
  const profileItem = useMemo(() => ({
    key: 'hb-profile',
    label: 'Profile',
    children: [{
      key: 'hb-profile-group',
      label: user?.DisplayName || 'Signed in',
      children: [
        { key: 'hb-open-profile', label: 'Profile', action: onProfile },
        { key: 'hb-logout', label: 'Log Out', action: onLogout },
      ],
    }],
  }), [user, onProfile, onLogout]);

  const hubOpen = hubItem && panel.isOpen(hubItem.key);
  const profileOpen = panel.isOpen('hb-profile');

  return (
    <>
      <header className="hb-nav" ref={navRef} onPointerMove={(e) => queuePointer(e.clientX, null)}>
        <div className="hb-sheen"><div className="hb-cursor-light" ref={lightRef} /></div>
        <svg className="hb-terraces" ref={terrRef} aria-hidden="true" />
        <div className="hb-pines-wrap"><svg className="hb-pines" ref={pinesRef} aria-hidden="true" /></div>
        <div className="hb-inner">
          <div className="hb-logo">HARBOR</div>
          {create && <div className="hb-create">{create}</div>}
          {trail('left', leftRef, leftItems)}
          <div className="hb-hub" ref={hubRef}>
            {hubItem && (
              <button
                type="button"
                ref={homeRef}
                className={`hb-home${selected.has(hubItem.key) ? ' hb-active' : ''}`}
                aria-label={hubItem.label}
                aria-haspopup="true"
                aria-expanded={!!hubOpen}
                onPointerEnter={(e) => {
                  const h = e.currentTarget;
                  if (h.classList.contains('hb-telling')) { clearTimeout(timers.current.back); clearInterval(timers.current.type); h.classList.remove('hb-telling'); }
                  glitch(); lift(homeBodyRef.current, true); hoverOpen(h, hubItem);
                }}
                onPointerMove={(e) => {
                  if (reduced) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  const rx = Math.max(-1, Math.min(1, (e.clientX - r.left - r.width / 2) / (r.width / 2)));
                  const ry = Math.max(-1, Math.min(1, (e.clientY - r.top - r.height / 2) / (r.height / 2)));
                  holoRef.current.style.setProperty('--ry', (rx * 14).toFixed(1) + 'deg');
                  holoRef.current.style.setProperty('--rx', (-ry * 12).toFixed(1) + 'deg');
                }}
                onPointerLeave={() => {
                  holoRef.current.style.setProperty('--ry', '0deg'); holoRef.current.style.setProperty('--rx', '0deg');
                  lift(homeBodyRef.current, false); scheduleClose();
                }}
                onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) openMenu(e.currentTarget, hubItem, 'click'); }}
                onClick={(e) => clickToggle(e.currentTarget, hubItem)}
              >
                <div className="hb-home-body" ref={homeBodyRef}>
                  <div className="hb-holo-float">
                    <div className="hb-holo" ref={holoRef}>
                      <div className="hb-holo-clip"><div className="hb-holo-lines" /></div>
                      <svg className="hb-holo-edge" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                        <path d="M9,0.5 H91 L99.5,24 V76 L91,99.5 H9 L0.5,76 V24 Z" strokeWidth="1" strokeOpacity=".75" />
                        <path d="M0.5,40 V24 L9,0.5 H24 M76,0.5 H91 L99.5,24 V40 M99.5,60 V76 L91,99.5 H76 M24,99.5 H9 L0.5,76 V60" strokeWidth="2" />
                      </svg>
                      <div className="hb-holo-face">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 20l1-11h2l1 11z" /><path d="M9.5 9h5M11 6h2v3h-2zM12 4v2M5 6.5l4 1.5M19 6.5l-4 1.5" /><path d="M3 21c1.5-1 3-1 4.5 0s3 1 4.5 0 3-1 4.5 0 3 1 4.5 0" /></svg>
                        <span>{hubItem.label}</span>
                      </div>
                      <div className="hb-holo-tip" aria-live="polite"><span className="hb-holo-tip-text"><span ref={tipRef} /><i className="hb-caret" /></span></div>
                    </div>
                  </div>
                </div>
              </button>
            )}
          </div>
          {trail('right', rightRef, rightItems)}
          <div className="hb-knobs">
            {extras && <div className="hb-extras">{extras}</div>}
            {recent && (
              <div
                className="hb-knob hb-bell"
                onPointerEnter={(e) => { lift(e.currentTarget, true); tell(RECENT_DESC); }}
                onPointerLeave={(e) => { lift(e.currentTarget, false); idleSoon(); }}
              >
                {recent}
              </div>
            )}
            {bell && (
              <div
                className="hb-knob hb-bell"
                onPointerEnter={(e) => { lift(e.currentTarget, true); tell(BELL_DESC); }}
                onPointerLeave={(e) => { lift(e.currentTarget, false); idleSoon(); }}
              >
                {bell}
              </div>
            )}
            <button
              type="button"
              className="hb-knob"
              aria-label="Profile"
              aria-haspopup="true"
              aria-expanded={!!profileOpen}
              onPointerEnter={(e) => { lift(e.currentTarget, true); tell(PROFILE_DESC); hoverOpen(e.currentTarget, profileItem); }}
              onPointerLeave={(e) => { lift(e.currentTarget, false); idleSoon(); scheduleClose(); }}
              onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) openMenu(e.currentTarget, profileItem, 'click'); }}
              onClick={(e) => clickToggle(e.currentTarget, profileItem)}
            >
              <span className="hb-cap">{initials}</span>
            </button>
          </div>
        </div>
      </header>
      <MenuPanel
        panel={panel}
        selected={selected}
        onNavigate={onNavigate}
        reduced={reduced}
        onItemEnter={(sec, c) => tell(c.action ? c.label : `${sec.label} › ${c.label}`)}
        onItemLeave={idleSoon}
        onPicked={idleSoon}
      />
    </>
  );
}
